// Sharing request preparation is pure/testable; authentication remains server-owned.
export const snapshots = new Map();
const configurationVersions = new Map();
const productVersions = new Map();
const clone = value => value === undefined ? undefined : JSON.parse(JSON.stringify(value));
function sharingError(message) {
  const error = new Error(message); error.error_message = message; return error;
}
export function requestId() {
  const bytes = new Uint32Array(4);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) crypto.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 0xffffffff);
  return Array.from(bytes, n => n.toString(16).padStart(8, '0')).join('');
}
export function malformed(value) {
  return !Array.isArray(value) || value.some(x => typeof x !== 'string' || !x) || new Set(value).size !== value.length;
}
export function capture(url, data) {
  if (/\/(backend|backoffice)\//.test(String(url))) return;
  if (data && data.sharing_state) {
    data.sharing_state.configurations.forEach(r => { configurationVersions.set(r._id, r.configuration_revision); snapshots.set(r._id, {expected: clone(r.branch_id), revision: r.membership_revision}); });
    Object.entries(data.sharing_state.products).forEach(([id, revision]) => productVersions.set(id, revision));
  }
  if (/\/(product|third_insurance|insurance)\/get/.test(String(url))) {
    const result = data && data.result;
    const records = Array.isArray(result) ? result : result && Array.isArray(result.data) ? result.data : result ? [result] : [];
    records.forEach(r => { if (r._id) productVersions.set(r._id, r.configuration_revision || 0); });
  }
  if (!String(url).includes('/sub_category/get_by_id')) return;
  const result = data && data.result;
  if (!result) return;
  const records = result.third_insurance ? [result.third_insurance, result.insurance] : [result];
  records.filter(Boolean).forEach(r => {
    if (r._id) r._loaded_membership = { expected: clone(r.branch_id), revision: r.membership_revision || 0 };
    if (r._id) snapshots.set(r._id, { expected: clone(r.branch_id), revision: r.membership_revision || 0 });
    if (r._id) configurationVersions.set(r._id, r.configuration_revision || 0);
  });
}
export function prepare(payload, paired = false, creating = false) {
  const next = clone(payload);
  const records = paired ? [next.third_insurance, next.insurance].filter(Boolean) : [next];
  const primary = paired ? next.third_insurance || next.insurance : next;
  const dirty = record => {
    if (!record || !record._membership_changed) return false;
    const before = record._loaded_membership || snapshots.get(record.sub_category_id || record._id);
    return !before || JSON.stringify(record.branch_id) !== JSON.stringify(before.expected);
  };
  const changed = dirty(primary);
  for (const record of records) {
    const identity = record.sub_category_id || record._id;
    record._configuration_revision = record.configuration_revision === undefined ? configurationVersions.get(identity) || 0 : record.configuration_revision;
    if (creating) {
      if (malformed(record.branch_id) || !record.branch_id.length) throw sharingError('Select at least one distinct branch.');
      delete record._membership_changed;
      delete record._loaded_membership;
      continue;
    }
    if (changed || dirty(record)) {
      const sid = record.sub_category_id || record._id;
      const before = record._loaded_membership || snapshots.get(sid);
      if (!before || malformed(before.expected)) throw sharingError('Legacy or unloaded membership cannot be changed. Reload or request review.');
      const selected = changed ? primary.branch_id : record.branch_id;
      if (malformed(selected) || !selected.length) throw sharingError('Select at least one distinct branch.');
      record._membership_edit = { ...clone(before), selected: clone(selected) };
    }
    delete record._membership_changed;
    delete record._loaded_membership;
  }
  return next;
}
export async function authorizeConfirmation(payload, context, confirm) {
  const affected = context.branches || [];
  const records = payload.third_insurance || payload.insurance ? [payload.third_insurance, payload.insurance].filter(Boolean) : [payload];
  const membershipChange = records.some(r => r._membership_edit);
  if (affected.length < 2 && !membershipChange) return true;
  const names = affected.map(b => b.name).join(', ');
  const message = membershipChange
    ? `Change participating branches? Affected branches: ${names}. Removing a branch prevents future product selection; historical documents remain unchanged.`
    : `This configuration is shared by ${affected.length} branches: ${names}. Changes to products, price, availability or details affect all participating branches.`;
  const result = await confirm({ title: membershipChange ? 'Confirm branch membership change' : 'Confirm shared product change', text: message });
  return Boolean(result && result.isConfirmed);
}
export function installSharing(axios, Vue) {
  axios.interceptors.request.use(async config => {
    if (!config.data || !config.data.token) return config;
    if (/\/(backend|backoffice)\//.test(String(config.url))) return config;
    const match = String(config.url || '').match(/\/(sub_category|product|third_insurance|insurance)\/([^/?]+)\s*$/);
    if (!match || /^(get|sharing_context|delete)/.test(match[2])) return config;
    const [path, family, action] = match;
    if (!/^(add|edit|changed|active)/.test(action)) return config;
    const creating = family === 'sub_category' && action.startsWith('add_');
    let payload = Vue.prototype.$jwt.decode(config.data.token);
    payload = prepare(payload, Boolean(payload.third_insurance || payload.insurance), creating);
    const records = payload.third_insurance || payload.insurance ? [payload.third_insurance, payload.insurance].filter(Boolean) : [payload];
    const query = {};
    if (creating) {
      if (!payload._creation_request_id) throw sharingError('Creation request ID missing. Reload the form.');
      query.new_branch_ids = records[0].branch_id;
    } else if (family === 'sub_category') {
      query.spc_ids = records.flatMap(r => Array.isArray(r.sub_category_id) ? r.sub_category_id : [r.sub_category_id || r._id]).filter(Boolean);
      query.include_pair = action === 'changed_insurance_sub_category_status';
    } else {
      query.collection = family;
      if (payload.sub_category_id) query.spc_ids = [payload.sub_category_id];
      else {
        const key = { product: 'product_id', insurance: 'insurance_id', third_insurance: 'third_insurance_id' }[family];
        query.product_ids = Array.isArray(payload[key]) ? payload[key] : [payload[key]];
      }
    }
    const base = config.url.slice(0, config.url.lastIndexOf(path));
    let capability;
    try {
      capability = await axios.post(`${base}/sub_category/sharing_capabilities`, {
        token: Vue.prototype.$jwt.sign({}, Vue.prototype.$privateKey, { noTimestamp: true }),
      });
    } catch (error) {
      throw sharingError('This Backend does not support safe catalogue sharing. Reload after the compatible rollout.');
    }
    if (!capability.result || capability.result.api_version !== 1 || !capability.result.writes_enabled) {
      throw sharingError('Catalogue writes are paused or the Backend version is incompatible. Reload after rollout.');
    }
    const response = await axios.post(`${base}/sub_category/sharing_context`, {
      token: Vue.prototype.$jwt.sign(query, Vue.prototype.$privateKey, { noTimestamp: true }),
    });
    const context = response.result;
    if (family === 'sub_category' && action.startsWith('changed_')) {
      payload._configuration_revisions = Object.fromEntries(context.configurations.map(r => [r._id, configurationVersions.has(r._id) ? configurationVersions.get(r._id) : r.configuration_revision]));
    }
    if (family !== 'sub_category') {
      const editedId = payload.product_id || payload.third_insurance_id || payload.insurance_id;
      payload._product_revisions = Object.fromEntries(Object.entries(context.product_revisions || {}).map(([id, revision]) => [id, id === editedId && payload.configuration_revision !== undefined ? payload.configuration_revision : productVersions.has(id) ? productVersions.get(id) : revision]));
      // Product constructors do not receive subcategory-editor concurrency metadata.
      delete payload._configuration_revision;
    }
    // Include newly selected branch names as well as existing members on membership edits.
    const requested = records.flatMap(r => r._membership_edit ? r._membership_edit.selected : []);
    if (requested.length) {
      const selection = await axios.post(`${base}/sub_category/sharing_context`, {
        token: Vue.prototype.$jwt.sign({ new_branch_ids: [...new Set(requested)] }, Vue.prototype.$privateKey, { noTimestamp: true }),
      });
      context.branches = [...new Map([...context.branches, ...selection.result.branches].map(b => [b._id, b])).values()];
    }
    if (!await authorizeConfirmation(payload, context, Vue.prototype.$alertConfirm)) {
      throw new axios.Cancel('Shared change cancelled');
    }
    config.headers = { ...config.headers, 'X-Permtang-Sharing-API': '1' };
    config.data = { ...config.data, token: Vue.prototype.$jwt.sign(payload, Vue.prototype.$privateKey, { noTimestamp: true }) };
    return config;
  });
}
export const SharingEditor = {
  data: () => ({ sharingSaving: false, sharingCreationRequestId: requestId(), sharingMemberNames: {}, sharingMembershipEditable: true, sharingManageableBranches: null }),
  methods: {
    isMalformedMembership: malformed,
    sharingBranchOptions(value, available) {
      const candidates = this.sharingManageableBranches === null || this.sharingManageableBranches === undefined ? available : this.sharingManageableBranches;
      const options = new Map((candidates || []).map(b => [b._id, b]));
      (Array.isArray(value) ? value : typeof value === 'string' ? [value] : []).filter(b => typeof b === 'string').forEach(id => {
        if (!options.has(id)) options.set(id, {_id:id,branch_name:this.sharingMemberNames[id] || `[Missing branch: ${id}]`});
      });
      return [...options.values()];
    },
    async loadSharingNames(id) {
      if (!id) return;
      this.sharingMembershipEditable = false;
      try {
        const response = await this.$axios.post(`${this.$baseUrl}/sub_category/sharing_context`, {token:this.$jwt.sign({spc_ids:[id]},this.$privateKey,{noTimestamp:true})});
        const data = response.result;
        data.branches.forEach(b => this.$set(this.sharingMemberNames,b._id,b.name));
        const allowed = (data.manageable_branches || []).map(b => b._id);
        this.sharingManageableBranches = data.manageable_branches || [];
        this.sharingMembershipEditable = data.configurations.every(c => !malformed(c.branch_id) && c.branch_id.every(b => allowed.includes(b)));
      } catch (error) {
        this.sharingMembershipEditable = false;
        this.$alertServerError({title:error.error_message || 'Cannot verify sharing permissions.'});
      }
    },
  },
};
